import React, { useState } from 'react'
import AddMessage from './AddMessage'
import { Accordion, AccordionTrigger, AccordionItem, AccordionContent } from 'src/components/ui/accordion'
import { isInterface } from 'src/functions/general/checks'
import { format } from 'date-fns'
import { Check, Loader2, Send, Trash2 } from 'lucide-react'
import { Input } from 'src/components/ui/input'
import TooltipForce from 'src/components/ui/tooltip-force'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from 'src/components/ui/tooltip'
import { useMutation } from 'react-query'
import axios from 'src/axios/axios'
import useAuth from 'src/hooks/useAuth'
import { useTranslation } from 'react-i18next'

type Props = { 
    communication?: Communcation[] | null
    onSuccess?: () => void
    document?: DocumentObject
    docid?: number
}

const Question = ({comm, onSuccess}:{comm:Communcation, onSuccess?:()=>void}) => {
    const { t } = useTranslation()
    const { auth } = useAuth()
    const [message, setmessage] = useState('');

    const { mutate:sendMessage, reset, isLoading } = useMutation({
        mutationFn: async () => {
            try {
                const { data } = await axios.post(`communications/message/add`, {
                    documentquestionheaderid: comm.documentsquestionheaderid,
                    message: message
                })
                if(data.errorcode!==0){ throw new Error(data.message) }
                reset();
                setmessage('')
                onSuccess && onSuccess()
            } catch (error) {
                console.log(error)
            }
        }
    })

    const { mutate:removeQuestion, isLoading:isLoadingRemove } = useMutation({
        mutationFn: async () => {
            try {
                const { data } = await axios.delete(`communications/header/delete?documentquestionheaderid=${comm.documentsquestionheaderid}`)
                if(data.errorcode!==0){ throw new Error(data.message) }
                reset();
                onSuccess && onSuccess()
            } catch (error) {
                console.log(error)
            }
        }
    })

    return (
        <AccordionItem value={`${comm.documentsquestionheaderid}`} className="w-full">
            <AccordionTrigger className="w-full hover:no-underline">
                <div className="flex flex-col gap-2 p-2 duration-300 rounded-md cursor-pointer">
                    <div className="flex items-center justify-between gap-4">
                        <div className="flex items-center gap-2">
                            <div className="flex items-center justify-center w-6 h-6 text-xs bg-white border rounded-full border-sky-200">{comm.Messages.Message.filter(m=>m.inforecord===false).length || 0}</div>
                            <p className="font-medium">{comm.createdby}</p>
                        </div>
                        <p className="text-xs">{t('Laatste bericht')} {comm.createdby} {format(new Date(comm.lastmodif), "dd/MM/yyyy - hh:mm")}</p>
                    </div>
                    <div className="flex items-center gap-2">
                        <p className="bg-primary/20 text-primary font-medium px-2 py-0.5 rounded-sm text-sm">
                            {comm.state.toString()==='1' && t('Open')}
                            {comm.state.toString()!=='1' && t('Gesloten')}
                        </p>
                        <p className="font-semibold">{comm.title}</p>
                    </div>
                </div>
            </AccordionTrigger>
            <AccordionContent>
                <div className='flex justify-end w-full mb-4'>
                    <div className='flex items-center justify-end p-1 bg-white border rounded-md'>
                        <TooltipProvider>
                            {/* {comm.state === 1 && <Tooltip delayDuration={0}><TooltipTrigger asChild><button className='flex items-center justify-center w-8 h-8 text-green-600 border border-transparent border-solid rounded-sm hover:border-neutral-200'><Check className='w-4 h-4'/></button></TooltipTrigger><TooltipContent><p className='whitespace-nowrap'>Markeren als beantwoord</p></TooltipContent></Tooltip>} */}
                            <Tooltip delayDuration={0}><TooltipTrigger asChild><button disabled={isLoadingRemove} onClick={()=>{removeQuestion()}} className='flex items-center justify-center w-8 h-8 text-red-600 border border-transparent border-solid rounded-sm hover:border-neutral-200'>{!isLoadingRemove ? <Trash2 className='w-4 h-4'/> : <Loader2 className='w-4 h-4 animate-spin'/> }</button></TooltipTrigger><TooltipContent><p className='whitespace-nowrap'>Bericht verwijderen</p></TooltipContent></Tooltip>
                        </TooltipProvider>
                    </div>
                </div>

                <div className="flex flex-col w-full gap-2">
                {comm.Messages.Message.map((message, index)=>{
                    const isInfo = message.inforecord
                    const infoClass = 'bg-yellow-200'
                    const messageClass = 'bg-blue-200'
                    return (
                    <div className={`${isInfo ? 'items-start' : 'items-end'} flex flex-col w-full`} key={message.documentsquestionrecordid}>
                        <p className="text-xs text-neutral-400">{message.username && `${message.username}:`} {format(new Date(message.datecreated), "dd/MM/yyyy")} - {message.timecreated.substring(0,5)}</p>
                        <div className={`${isInfo ? infoClass : messageClass} font-medium border px-2 py-1 w-fit rounded-full`} key={message.documentsquestionrecordid}>
                            <p>{message.messagerecord}</p>
                        </div>
                    </div>
                    )
                })}
                </div>

                <div className="flex items-center gap-2 mt-4">
                    <Input value={message} onChange={({currentTarget})=>{setmessage(currentTarget.value)}} placeholder={`${t('Typ een bericht')}...`} className={`w-full rounded-full`}/>
                    <button onClick={()=>{sendMessage()}} disabled={isLoading} className={`${message.length > 1 ? 'w-[40px] px-3 ' : 'w-[0px] px-0 pointer-events-none'} h-10 bg-primary text-primary-foreground rounded-full`}>
                        {!isLoading ? <Send className="w-4 h-4"/> : <Loader2 className='w-4 h-4 animate-spin'/>}
                    </button>
                </div>
            </AccordionContent>
        </AccordionItem>
    )
}

const Communication = ({communication, document, docid, onSuccess}:Props) => {
  return (
    <>
        <div className="flex items-center justify-end gap-2">
            {document && <AddMessage docid={docid ? docid : document.docid} onSuccess={onSuccess} />}
        </div>

        <Accordion type="single" className="w-full" collapsible>
        {isInterface(communication, {} as Communcation[]) && communication.map((comm, ind)=>(
            <Question comm={comm} key={comm.documentsquestionheaderid} onSuccess={()=>{onSuccess && onSuccess()}} />
        ))}
        </Accordion>
    </>
  )
}

export default Communication